import {
  AccountTree as AccountTreeIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FormatListBulleted as FormatListBulletedIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as PlayArrowIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { SectionHeader } from "@/components/ui";
import { STATUS_COLORS } from "@/theme/theme";
import CheckDependencyGraph from "../components/checks/check-dependency-graph.tsx";
import CheckScheduleDialog from "../components/checks/check-schedule-dialog.tsx";
import CheckScheduleRunsTable from "../components/checks/check-schedule-runs-table.tsx";
import { PageHeader } from "../components/page-header";
import { RunStatusChip } from "../components/run-status-chip.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import type { Check, CheckSchedule, CheckScheduleSchema } from "../types.ts";

interface CheckScheduleDetailsTabs {
  tab?: "config" | "checks" | "runs";
  run_id?: string;
}

interface ChecksListResponse {
  checks: Check[];
  total: number;
}

/**
 * Derive layered execution order from a `check_dependency_graph`.
 * Graph shape: { dep_id: [dependent_ids], "root": [layer-0 ids] }.
 * Returns an array of layers, each a list of check IDs that run together.
 * Falls back to a single layer with all `checkIds` when no usable graph exists.
 */
function computeDependencyLayers(
  graph: Record<string, string[]> | null | undefined,
  checkIds: string[],
): string[][] {
  if (!graph || Object.keys(graph).length === 0) {
    return checkIds.length > 0 ? [checkIds] : [];
  }

  // Build dependency-count (in-degree) per check from the dependent edges.
  const inDegree = new Map<string, number>();
  for (const id of checkIds) inDegree.set(id, 0);
  for (const [from, dependents] of Object.entries(graph)) {
    if (from === "root") continue;
    for (const dependent of dependents) {
      inDegree.set(dependent, (inDegree.get(dependent) ?? 0) + 1);
    }
  }

  const layers: string[][] = [];
  const remaining = new Set(checkIds);

  while (remaining.size > 0) {
    const layer = [...remaining].filter((id) => (inDegree.get(id) ?? 0) === 0);
    if (layer.length === 0) {
      // Cycle or orphan — append whatever is left so nothing is dropped.
      layers.push([...remaining]);
      break;
    }
    layers.push(layer);
    for (const id of layer) {
      remaining.delete(id);
      for (const dependent of graph[id] ?? []) {
        inDegree.set(dependent, (inDegree.get(dependent) ?? 1) - 1);
      }
    }
  }

  return layers;
}

// Human-readable label for a schedule failure strategy.
function failureStrategyLabel(strategy: string | null | undefined): string {
  switch (strategy) {
    case "stop_on_failure":
      return "Stop on failure";
    case "continue_on_failure":
      return "Continue on failure";
    default:
      return "Continue on failure";
  }
}

export default function CheckScheduleDetailsPage() {
  const { scheduleId } = useParams();
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { params, setParam, removeParam } = useTypedQueryParams<CheckScheduleDetailsTabs>();
  const tab = params.tab ?? "config";
  const selectedRunId = params.run_id;

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setParam("tab", newValue as CheckScheduleDetailsTabs["tab"]);
  };

  const handleRunSelect = (runId: string) => {
    setParam("run_id", runId);
  };

  const handleCloseRunDrawer = () => {
    removeParam("run_id");
  };

  // Auto-switch to runs tab when run_id is provided via URL
  useEffect(() => {
    if (selectedRunId && tab !== "runs") {
      setParam("tab", "runs");
    }
  }, [selectedRunId, tab, setParam]);

  // Edit dialog state
  const [editData, setEditData] = useState<CheckScheduleSchema | CheckSchedule | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Checks tab: graph (default) vs. list view.
  const [checksView, setChecksView] = useState<"graph" | "list">("graph");

  const {
    data: schedule,
    isLoading: isLoadingSchedule,
    refetch: refetchSchedule,
  } = useApiQuery<CheckSchedule>(`/check-schedules/${scheduleId}`, {
    enabled: !!scheduleId,
    refetchInterval: 5000,
  });

  // Fetch all checks to display their names
  const { data: checksResponse } = useApiQuery<ChecksListResponse>(
    schedule?.app_config_id ? `/checks?app_config_id=${schedule.app_config_id}` : "/checks",
    {
      enabled: !!schedule?.app_config_id,
    },
  );

  const allChecks = checksResponse?.checks ?? [];
  const includedChecks = allChecks.filter((c) => (schedule?.check_ids || []).includes(c.id));

  // Group included checks into dependency-ordered layers for display.
  const checkById = new Map(includedChecks.map((c) => [c.id, c]));
  const dependencyLayers = computeDependencyLayers(
    schedule?.check_dependency_graph,
    schedule?.check_ids || [],
  )
    .map((layer) => layer.map((id) => checkById.get(id)).filter((c): c is Check => !!c))
    .filter((layer) => layer.length > 0);

  const { mutate: updateSchedule, isPending: isUpdatingSchedule } = useApiMutation<
    Partial<CheckScheduleSchema>,
    { status: string }
  >("PATCH", `/check-schedules/${scheduleId}`, {
    onSuccess: () => {
      enqueueSnackbar("Schedule updated!", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      refetchSchedule();
      setEditData(null);
    },
  });

  const { mutate: runSchedule, isPending: isRunningSchedule } = useApiMutation<
    void,
    { run_id: string }
  >("POST", `/check-schedules/${scheduleId}/run`, {
    onSuccess: () => {
      enqueueSnackbar(
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body1">Schedule run started!</Typography>
          <IconButton
            aria-label="View runs"
            onClick={() => setParam("tab", "runs")}
            size="small"
            color="inherit"
          >
            <OpenInNewIcon sx={{ color: "common.white" }} />
          </IconButton>
        </Stack>,
        {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );
      setParam("tab", "runs");
      refetchSchedule();
      // Immediately refetch runs to show the new running schedule
      queryClient.invalidateQueries({
        queryKey: [`/check-schedules/${scheduleId}/runs`],
      });
    },
  });

  const { mutate: deleteSchedule, isPending: isDeletingSchedule } = useApiMutation<void, void>(
    "DELETE",
    `/check-schedules/${scheduleId}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Schedule deleted!", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        nav("/check-schedules");
      },
    },
  );

  const handleEdit = () => {
    if (schedule) {
      setEditData(schedule);
    }
  };

  const handleSaveEdit = (data: CheckScheduleSchema) => {
    updateSchedule(data);
  };

  const handleDeleteConfirm = () => {
    deleteSchedule({});
  };

  if (isLoadingSchedule) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading schedule...</Typography>
      </Box>
    );
  }

  if (!schedule) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Schedule not found</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        flexDirection: "column",
        display: "flex",
        overflow: "hidden",
      }}
    >
      <PageHeader
        title={
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                letterSpacing: "-0.02em",
              }}
            >
              {schedule.name}
            </Typography>
            <Chip
              label={schedule.enabled ? "Active" : "Paused"}
              size="small"
              sx={{
                fontSize: "0.7rem",
                height: 22,
                backgroundColor: schedule.enabled ? STATUS_COLORS.success.bg : "grey.100",
                color: schedule.enabled ? STATUS_COLORS.success.text : "grey.600",
              }}
            />
            <Chip
              label={schedule.cron_expression}
              size="small"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.7rem",
                height: 22,
                backgroundColor: "grey.100",
              }}
            />
          </Stack>
        }
        subtitle={schedule.description || "Scheduled check execution"}
        actions={
          <>
            <Tooltip title="Edit schedule" placement="top">
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete schedule" placement="top">
              <IconButton
                size="small"
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  color: "error.main",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: STATUS_COLORS.error.bg,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Run schedule now" placement="top">
              <IconButton
                size="small"
                disabled={isRunningSchedule}
                onClick={() => runSchedule({})}
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  color: "grey.800",
                  backgroundColor: "transparent",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "common.white",
                  },
                }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      {/* Edit Dialog */}
      {editData && (
        <CheckScheduleDialog
          open={!!editData}
          mode="edit"
          onClose={() => setEditData(null)}
          onSave={handleSaveEdit}
          data={editData}
          setData={setEditData}
          isProcessing={isUpdatingSchedule}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Schedule Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the schedule "{schedule.name}"? This action will soft
            delete the schedule and it will no longer be visible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeletingSchedule}>
            {isDeletingSchedule ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange} aria-label="Schedule details tabs">
            <Tab label="Configuration" value="config" />
            <Tab label={`Checks (${schedule.check_ids?.length || 0})`} value="checks" />
            <Tab label="Run History" value="runs" />
          </TabList>
        </Box>

        <TabPanel value="config" sx={{ flex: 1, overflow: "auto" }}>
          <Stack spacing={3}>
            {/* Schedule Configuration */}
            <Paper sx={{ p: 3 }}>
              <SectionHeader>Schedule Configuration</SectionHeader>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    CRON Expression
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ fontFamily: "monospace" }}>
                    {schedule.cron_expression}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Timezone
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {schedule.timezone || "UTC"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={schedule.enabled ? "Active" : "Paused"}
                      size="small"
                      sx={{
                        backgroundColor: schedule.enabled ? STATUS_COLORS.success.bg : "grey.100",
                        color: schedule.enabled ? STATUS_COLORS.success.text : "grey.600",
                      }}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Failure Strategy
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {failureStrategyLabel(schedule.failure_strategy)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="caption" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {schedule.description || "—"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Metadata */}
            <Paper sx={{ p: 3 }}>
              <SectionHeader>Metadata</SectionHeader>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Checks
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {schedule.check_ids?.length || 0}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {new Date(schedule.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {schedule.updated_at ? new Date(schedule.updated_at).toLocaleString() : "-"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </TabPanel>

        <TabPanel value="checks" sx={{ flex: 1, overflow: "auto" }}>
          <Paper sx={{ p: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <SectionHeader>Included Checks</SectionHeader>
              {includedChecks.length > 0 && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={checksView}
                  onChange={(_, v) => v && setChecksView(v)}
                  aria-label="Checks view mode"
                >
                  <ToggleButton value="graph" aria-label="Graph view">
                    <AccountTreeIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Graph
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="List view">
                    <FormatListBulletedIcon fontSize="small" sx={{ mr: 0.5 }} />
                    List
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Stack>
            {includedChecks.length === 0 ? (
              <Typography color="text.secondary">
                No checks are included in this schedule. Edit the schedule to add checks.
              </Typography>
            ) : checksView === "graph" ? (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Checks run in dependency order — arrows point from a check to the checks that
                  depend on it.
                </Typography>
                <CheckDependencyGraph
                  dependencyGraph={schedule.check_dependency_graph}
                  checks={includedChecks.map((c) => ({ id: c.id, name: c.name }))}
                />
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" mb={2} mt={2}>
                  {dependencyLayers.length > 1
                    ? "Checks run in dependency order — layers run sequentially, checks within a layer run in parallel."
                    : "All checks run in parallel — none declare dependencies on each other."}
                </Typography>
                <Stack spacing={2}>
                  {dependencyLayers.map((layer, layerIndex) => (
                    <Box key={layerIndex}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Chip
                          label={`Layer ${layerIndex + 1}`}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            fontWeight: 600,
                            backgroundColor: "grey.100",
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {layer.length} check{layer.length === 1 ? "" : "s"}
                          {layerIndex > 0 ? " · runs after previous layer" : ""}
                        </Typography>
                      </Stack>
                      <List disablePadding>
                        {layer.map((check) => (
                          <ListItem
                            key={check.id}
                            sx={{
                              borderRadius: 1,
                              mb: 1,
                              backgroundColor: "background.default",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "grey.200",
                              },
                            }}
                            onClick={() => nav(`/checks/${check.id}`)}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              {check.run_status === "passed" ? (
                                <CheckCircleIcon sx={{ color: STATUS_COLORS.success.text }} />
                              ) : check.run_status === "failed" ? (
                                <RadioButtonUncheckedIcon sx={{ color: "error.dark" }} />
                              ) : (
                                <RadioButtonUncheckedIcon sx={{ color: "grey.500" }} />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={check.name}
                              secondary={
                                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                  <Chip
                                    label={check.check_type.toUpperCase()}
                                    size="small"
                                    sx={{
                                      fontSize: "0.65rem",
                                      height: 18,
                                      backgroundColor: STATUS_COLORS.info.bg,
                                      color: STATUS_COLORS.info.text,
                                    }}
                                  />
                                  {check.run_status && <RunStatusChip status={check.run_status} />}
                                  <Typography variant="caption" color="text.secondary">
                                    {check.config?.method} {check.config?.url}
                                  </Typography>
                                </Stack>
                              }
                            />
                            <OpenInNewIcon sx={{ color: "grey.500", fontSize: 18 }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </TabPanel>

        <TabPanel value="runs" sx={{ flex: 1, overflow: "hidden", p: 0 }}>
          <CheckScheduleRunsTable
            scheduleId={scheduleId!}
            runId={selectedRunId}
            checkDependencyGraph={schedule.check_dependency_graph}
            onRunSelect={handleRunSelect}
            onCloseRunDrawer={handleCloseRunDrawer}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
